import { Funnel, FunnelChart, ResponsiveContainer, Tooltip } from 'recharts';
import { chartColors } from '@shared/lib/chart-theme';
import { ChartTooltip } from '@features/analytics/ui/ChartTooltip';
import { formatCount, formatPercent } from '@features/engagement/model/engagement.types';

export interface FunnelStage {
  label: string;
  /** People still standing at this stage. */
  value: number;
  /** What reaching this stage actually means. */
  hint: string;
}

/**
 * The signup-to-real-use funnel, drawn as one.
 *
 * The tapering shape is the point: the narrowing *is* the finding, and it lands
 * before a number is read. Stages are an ordered sequence, so the trapezoids
 * take the ordinal ramp.
 *
 * No labels inside the shape. The last stage is a fifteenth the width of the
 * first, so anything set in it collides with its neighbours or spills over the
 * edge — the key below carries every stage name, count and drop instead, keyed
 * by the same colours.
 */
export function UsageFunnel({ stages }: { stages: FunnelStage[] }) {
  const total = stages[0]?.value ?? 0;

  const colourOf = (index: number) =>
    chartColors.ordinal[Math.min(index, chartColors.ordinal.length - 1)] as string;

  const points = stages.map((stage, index) => ({
    name: stage.label,
    value: stage.value,
    fill: colourOf(index),
  }));

  return (
    <div>
      <ResponsiveContainer width="100%" height={132}>
        <FunnelChart margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
          <Tooltip content={<ChartTooltip />} />
          <Funnel
            dataKey="value"
            data={points}
            isAnimationActive={false}
            // The white edge is the 2px surface gap between adjacent fills.
            stroke="#fff"
            strokeWidth={2}
          />
        </FunnelChart>
      </ResponsiveContainer>

      <ul className="mt-2 space-y-1.5">
        {stages.map((stage, index) => {
          const previous = stages[index - 1];

          return (
            <li key={stage.label} className="flex items-baseline gap-2 text-xs">
              <span
                aria-hidden
                className="h-2 w-2 shrink-0 translate-y-[-1px] rounded-full"
                style={{ backgroundColor: colourOf(index) }}
              />
              <span className="min-w-0 truncate">
                <span className="text-slate-700">{stage.label}</span>
                <span className="ml-1.5 text-slate-400">{stage.hint}</span>
              </span>
              <span className="ml-auto shrink-0 tabular-nums">
                <span className="font-semibold text-slate-900">{formatCount(stage.value)}</span>
                <span className="ml-1.5 text-slate-400">
                  {formatPercent(total > 0 ? stage.value / total : 0)}
                </span>
                {previous && (
                  <span className="ml-1.5 text-amber-700">
                    −{formatCount(previous.value - stage.value)}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
